import { useState, useRef, useCallback, useEffect } from 'react'

// Web Speech API is not in lib.dom typings; the constructor is feature-detected below.
type SpeechRecognitionInstance = { start(): void; stop(): void; abort(): void } | null

interface UseVoiceInputOptions {
  /** Language BCP-47 tag, defaults to browser locale */
  lang?: string
  /** Append interim results to the callback while still recording */
  onInterim?: (text: string) => void
  /** Called with final transcript when recognition ends */
  onResult: (text: string) => void
  /**
   * Called with each newly FINALISED segment, as it lands.
   *
   * `onInterim`/`onResult` hand back the whole transcript, which suits a chat
   * box you dictate into from empty. Dictating into something that already
   * has text in it — a diary entry half typed — needs the delta instead, or
   * every pause would overwrite what is already there.
   */
  onChunk?: (text: string) => void
  /**
   * Keep listening across silences until `stop()` is called.
   *
   * Chrome ends a `continuous` session by itself after a few seconds of quiet
   * and fires `onend`. For dictation that is fatal in the ordinary case: a
   * pause to think is not the end of what someone was saying. With this on,
   * `onend` re-arms unless the person stopped it themselves.
   */
  autoRestart?: boolean
}

interface UseVoiceInputReturn {
  isListening: boolean
  isSupported: boolean
  /** The last recognition error, in the API's own words ('not-allowed', …). */
  error: string | null
  start: () => void
  stop: () => void
  toggle: () => void
}

/**
 * Looked up when it is USED, not when this module is imported.
 *
 * Capturing it at module scope meant the answer was fixed at the moment the
 * bundle evaluated — which is also why the feature could not be exercised
 * without a real microphone and a live speech service. A function can be
 * stood in front of.
 */
const getSpeechRecognition = (): any =>
  (typeof window !== 'undefined' &&
    ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)) ||
  null

export function useVoiceInput(
  { lang, onInterim, onResult, onChunk, autoRestart = false }: UseVoiceInputOptions,
): UseVoiceInputReturn {
  const [isListening, setIsListening] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const recognitionRef = useRef<SpeechRecognitionInstance>(null)
  const finalTranscript = useRef('')
  // Whether the PERSON wants the mic on. `onend` fires for silence, for a
  // network blip and for a real stop; only this says which.
  const wanted = useRef(false)

  // Handlers live in refs so a re-render cannot leave the running recogniser
  // calling last render's closures — and so auto-restart does not need to
  // rebuild the recogniser to pick up a new callback.
  const handlers = useRef({ onInterim, onResult, onChunk })
  handlers.current = { onInterim, onResult, onChunk }

  const isSupported = getSpeechRecognition() !== null

  const stop = useCallback(() => {
    wanted.current = false
    recognitionRef.current?.stop()
  }, [])

  const begin = useCallback(() => {
    const Ctor = getSpeechRecognition()
    if (!Ctor) return
    const recognition = new Ctor()
    recognition.lang = lang || navigator.language || 'en-US'
    recognition.interimResults = Boolean(handlers.current.onInterim || handlers.current.onChunk)
    recognition.continuous = true
    recognition.maxAlternatives = 1
    recognitionRef.current = recognition

    recognition.onresult = (event: any) => {
      let interim = ''
      let final = ''
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) final += result[0].transcript
        else interim += result[0].transcript
      }
      finalTranscript.current = final
      handlers.current.onInterim?.(final + interim)
      // Only the results from this event, and only the finalised ones: the
      // delta a caller can append.
      if (handlers.current.onChunk) {
        let chunk = ''
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) chunk += event.results[i][0].transcript
        }
        if (chunk.trim()) handlers.current.onChunk(chunk.trim())
      }
    }

    recognition.onerror = (event: any) => {
      // 'no-speech' is a silence, not a failure, and 'aborted' is us. Anything
      // else the person needs told — a blocked microphone looks exactly like a
      // broken button otherwise.
      if (event.error === 'no-speech' || event.error === 'aborted') return
      setError(event.error || 'speech-error')
      wanted.current = false
      setIsListening(false)
    }

    recognition.onend = () => {
      const text = finalTranscript.current.trim()
      if (autoRestart && wanted.current) {
        // Re-arm. Chrome throws if start() lands while the old session is
        // still tearing down; a failure here means the mic is genuinely gone,
        // so stop pretending it is on.
        try {
          recognition.start()
          return
        } catch {
          wanted.current = false
        }
      }
      setIsListening(false)
      if (text) handlers.current.onResult(text)
    }

    recognition.start()
  }, [autoRestart, lang])

  const start = useCallback(() => {
    if (!getSpeechRecognition()) return
    recognitionRef.current?.abort()
    finalTranscript.current = ''
    setError(null)
    wanted.current = true
    try {
      begin()
      setIsListening(true)
    } catch (e) {
      wanted.current = false
      setError((e as Error).message || 'speech-error')
    }
  }, [begin])

  const toggle = useCallback(() => {
    if (isListening) stop()
    else start()
  }, [isListening, start, stop])

  useEffect(() => {
    return () => {
      // Leaving the page releases the microphone. `abort` rather than `stop`:
      // there is nobody left to hand a final transcript to.
      wanted.current = false
      recognitionRef.current?.abort()
    }
  }, [])

  return { isListening, isSupported, error, start, stop, toggle }
}
